/**
 * 原生功能桥接服务
 * 提供与原生应用交互的接口
 */
import geolocationService from "../services/GeolocationService";

export interface NativeCameraResult {
  success: boolean;
  data?: string; // base64图片数据
  path?: string; // 文件路径
  error?: string;
}

export interface PermissionStatus {
  granted: boolean;
  reason?: string;
}

export interface ImageCompressionOptions {
  maxWidth?: number;     // 最大宽度，默认1080
  maxHeight?: number;    // 最大高度，默认1080  
  quality?: number;      // JPEG压缩质量 (10-100)，默认75
  enableResize?: boolean; // 是否启用尺寸调整，默认true
}


// 定义全局接口，用于原生应用注入桥接方法
declare global {
  interface Window {
    SwiftXBridge?: {
      // 相机相关
      openCamera: (options: {type: 'front'|'back'} & ImageCompressionOptions) => Promise<NativeCameraResult>;
      openGallery: (options?: ImageCompressionOptions) => Promise<NativeCameraResult>;
      
      // 权限相关
      checkPermission: (permission: 'camera'|'storage'|'location') => Promise<PermissionStatus>;
      requestPermission: (permission: 'camera'|'storage'|'location') => Promise<PermissionStatus>;
      
      // 存储相关
      saveToGallery: (base64: string, filename: string) => Promise<{success: boolean, path?: string, error?: string}>;
    };
  }
}

window.SwiftXBridge = {
  checkPermission: function(permission) {
    return new Promise(function(resolve) {
      if (window.AndroidApp && window.AndroidApp.checkSwiftXBridgePermission) {
        var result = window.AndroidApp.checkSwiftXBridgePermission(permission);
        resolve(JSON.parse(result));
      } else {
        resolve({granted: false, reason: 'Native bridge not available'});
      }
    });
  },
  requestPermission: function(permission) {
    return new Promise(function(resolve) {
      if (!window.AndroidApp || !window.AndroidApp.requestSwiftXBridgePermission) {
        resolve({granted: false, reason: 'Native bridge not available'});
        return;
      }
      var callbackName = '_swiftx_permission_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      window[callbackName] = function(result: {granted: boolean, reason?: string}) {
        delete window[callbackName];
        resolve(result);
      };
      window.AndroidApp.requestSwiftXBridgePermission(permission, callbackName);
    });
  },
  openCamera: function(options) {
    return new Promise(function(resolve) {
        if (!window.AndroidApp || !window.AndroidApp.openSwiftXBridgeCamera) {
            resolve({success: false, error: 'Native bridge not available'});
            return;
        }
      const callbackName = '_swiftx_camera_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      window[callbackName] = function(result: {success: boolean, data?: string, path?: string, error?: string}) {
        delete window[callbackName];
        resolve(result);
      };
      window.AndroidApp.openSwiftXBridgeCamera(JSON.stringify(options), callbackName);
    });
  },
  openGallery: function(options = {}) {
    return new Promise(function(resolve) {
      if (!window.AndroidApp || !window.AndroidApp.openSwiftXBridgeGallery) {
        resolve({success: false, error: 'Native bridge not available'});
        return;
      }
      const callbackName = '_swiftx_gallery_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      window[callbackName] = function(result: {success: boolean, data?: string, path?: string, error?: string}) {
        delete window[callbackName];
        resolve(result);
      };
      window.AndroidApp.openSwiftXBridgeGallery(JSON.stringify(options), callbackName);
    });
  },
  saveToGallery: function(base64, filename) {
    return new Promise(function(resolve) {
      resolve({success: false, error: 'Save to gallery not implemented yet'});
    });
  }
};

/**
 * 检查原生桥接是否可用
 */
export const isNativeBridgeAvailable = (): boolean => {
  return !!window.AndroidApp;
};

/**
 * 检查是否运行在WebView环境中
 */
const isWebView = (): boolean => {
  const userAgent = navigator.userAgent;
  // 检查常见的WebView标识
  return /wv|WebView|iPhone.*Mobile|iPad.*Mobile/i.test(userAgent) && 
         !/Chrome\/[.0-9]* Mobile/i.test(userAgent) &&
         !/Safari\/[.0-9]*/i.test(userAgent);
};

/**
 * 检查设备是否支持媒体设备API
 */
const hasMediaDevicesSupport = (): boolean => {
  return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
};

/**
 * 相机服务
 */
export const NativeCamera = {
  /**
   * 打开原生相机
   * @param useFrontCamera 是否使用前置相机
   * @param compressionOptions 图片压缩选项
   * @returns Promise<string> 返回图片base64数据
   */
  openCamera: async (useFrontCamera: boolean = false, compressionOptions?: ImageCompressionOptions): Promise<string> => {
    if (!isNativeBridgeAvailable()) {
      throw new Error('Native bridge not available');
    }
    
    try {
      const result = await window.SwiftXBridge!.openCamera({
        type: useFrontCamera ? 'front' : 'back',
        ...compressionOptions
      });
      
      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to capture photo');
      }
      
      return result.data;
    } catch (error) {
      console.error('Failed to open native camera:', error);
      throw error;
    }
  },
  
  /**
   * 打开原生相册选择图片
   * @param compressionOptions 图片压缩选项
   * @returns Promise<string> 返回图片base64数据
   */
  openGallery: async (compressionOptions?: ImageCompressionOptions): Promise<string> => {
    if (!isNativeBridgeAvailable()) {
      throw new Error('Native bridge not available');
    }
    
    try {
      const result = await window.SwiftXBridge!.openGallery(compressionOptions);
      
      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to select photo from gallery');
      }
      
      return result.data;
    } catch (error) {
      console.error('Failed to open native gallery:', error);
      throw error;
    }
  },
  
  /**
   * 保存图片到相册
   * @param base64 图片base64数据
   * @param filename 文件名
   * @returns Promise<boolean> 是否保存成功
   */
  saveToGallery: async (base64: string, filename: string): Promise<boolean> => {
    if (!isNativeBridgeAvailable()) {
      throw new Error('Native bridge not available');
    }
    
    try {
      const result = await window.SwiftXBridge!.saveToGallery(base64, filename);
      return result.success;
    } catch (error) {
      console.error('Failed to save to gallery:', error);
      throw error;
    }
  }
};

/**
 * 权限服务
 */
export const NativePermissions = {
  /**
   * 检查权限状态
   * @param permission 权限类型
   * @returns Promise<boolean> 是否已授权
   */
  check: async (permission: 'camera'|'storage'|'location'): Promise<boolean> => {
    // 如果原生桥接可用，优先使用原生桥接检查权限
    if (isNativeBridgeAvailable()) {
      try {
        const status = await window.SwiftXBridge!.checkPermission(permission);
        return status.granted;
      } catch (error) {
        console.error(`Failed to check ${permission} permission:`, error);
        return false;
      }
    }
    
    // 网页环境下的权限检查
    if (permission === 'camera') {
      // 在WebView中，避免直接调用getUserMedia来检查权限
      if (isWebView()) {
        // WebView环境下，假设权限需要由原生应用处理
        console.warn('Camera permission check in WebView should be handled by native bridge');
        return false;
      }
      
      // 浏览器环境下，使用权限API检查
      if ('permissions' in navigator) {
        try {
          const permissionStatus = await navigator.permissions.query({
            name: 'camera' as any
          });
          return permissionStatus.state === 'granted';
        } catch (error) {
          console.warn('Camera permission API not supported', error);
        }
      }
      
      // 如果权限API不支持，检查是否有媒体设备支持
      return hasMediaDevicesSupport();
    }
    
    if (permission === 'location') {
      if ('permissions' in navigator) {
        try {
          const permissionStatus = await navigator.permissions.query({
            name: 'geolocation'
          });
          return permissionStatus.state === 'granted';
        } catch (error) {
          console.warn('Geolocation permission API not supported', error);
        }
      }
      
      // 检查是否支持地理位置API
      return 'geolocation' in navigator;
    }
    
    return false;
  },
  
  /**
   * 请求权限
   * @param permission 权限类型
   * @returns Promise<boolean> 是否授权成功
   */
  request: async (permission: 'camera'|'storage'|'location'): Promise<boolean> => {
    // 如果原生桥接可用，优先使用原生桥接请求权限
    if (isNativeBridgeAvailable()) {
      try {
        const status = await window.SwiftXBridge!.requestPermission(permission);
        return status.granted;
      } catch (error) {
        console.error(`Failed to request ${permission} permission:`, error);
        return false;
      }
    }
    
    // 网页环境下的权限请求
    if (permission === 'camera') {
      // 在WebView中，不应该直接请求相机权限
      if (isWebView()) {
        console.error('Camera permission in WebView must be handled by native application');
        throw new Error('Camera permission requires native bridge in WebView environment');
      }
      
      // 浏览器环境下，尝试访问相机来触发权限请求
      if (hasMediaDevicesSupport()) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ 
            video: true,
            audio: false
          });
          // 立即停止所有轨道
          stream.getTracks().forEach(track => track.stop());
          return true;
        } catch (error) {
          console.error('Failed to request camera permission:', error);
          return false;
        }
      }
      
      return false;
    }
    
    // 对于位置权限的处理
    if (permission === 'location') {
      if ('geolocation' in navigator) {
        try {
          await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
                resolve,
                reject,
                { timeout: 5000 }
            );
          });
          return true;
        } catch (error) {
          console.error('Failed to request location permission:', error);
          return false;
        }
      }
      
      return false;
    }
    
    return false;
  }
};

export default {
  isAvailable: isNativeBridgeAvailable,
  camera: NativeCamera,
  permissions: NativePermissions
}; 